import { useCallback, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { CreationPreview } from '@/components/CreationPreview';
import { Note } from '@/components/Note';
import { TrashZone } from '@/components/TrashZone';
import { useDrag } from '@/hooks/useDrag';
import { useOrderedNotes } from '@/hooks/useNotes';
import { useNotesDispatch } from '@/hooks/useNotesDispatch';
import { useInteraction } from '@/hooks/useInteraction';
import { create } from '@/state/notesActions';
import type { Position, Rect } from '@/types/geometry';
import type { NoteColor } from '@/enums/noteColor';
import { newNoteId } from '@/utils/id';
import { normalizeDragRect, sizeSatisfies } from '@/utils/geometry';
import { createNoteInputSchema } from '@/validation/noteSchema';
import { MIN_CREATE_DRAG_DISTANCE, MIN_NOTE_SIZE } from '@/constants/notes';
import styles from './Board.module.css';

interface CreateCache {
  readonly startClient: Position;
  readonly boardOffset: Position;
}

interface Props {
  readonly selectedColor: NoteColor;
}

export function Board({ selectedColor }: Props) {
  const dispatch = useNotesDispatch();
  const notes = useOrderedNotes();
  const { boardRef } = useInteraction();

  const [preview, setPreview] = useState<Rect | null>(null);

  const clientToBoard = useCallback(
    (client: Position, offset: Position): Position => ({
      x: client.x - offset.x,
      y: client.y - offset.y,
    }),
    [],
  );

  const startCreate = useCallback(
    (event: ReactPointerEvent<HTMLElement>): CreateCache => {
      const rect = event.currentTarget.getBoundingClientRect();
      return {
        startClient: { x: event.clientX, y: event.clientY },
        boardOffset: { x: rect.left, y: rect.top },
      };
    },
    [],
  );

  const duringCreate = useCallback(
    ({ current, cache }: { current: Position; cache: CreateCache }) => {
      const boardStart = clientToBoard(cache.startClient, cache.boardOffset);
      const boardCurrent = clientToBoard(current, cache.boardOffset);
      setPreview(normalizeDragRect(boardStart, boardCurrent));
    },
    [clientToBoard],
  );

  const finishCreate = useCallback(
    ({ current, cache, distance }: { current: Position; cache: CreateCache; distance: number }) => {
      setPreview(null);
      if (distance < MIN_CREATE_DRAG_DISTANCE) return;
      const boardStart = clientToBoard(cache.startClient, cache.boardOffset);
      const boardCurrent = clientToBoard(current, cache.boardOffset);
      const rect = normalizeDragRect(boardStart, boardCurrent);
      if (!sizeSatisfies(rect, MIN_NOTE_SIZE)) return;

      const input = {
        x: rect.x,
        y: rect.y,
        width: Math.max(rect.width, MIN_NOTE_SIZE.width),
        height: Math.max(rect.height, MIN_NOTE_SIZE.height),
        color: selectedColor,
        text: '',
      };
      const validated = createNoteInputSchema(input);
      if (!validated.ok) return;

      dispatch(
        create({
          id: newNoteId(),
          x: validated.value.x,
          y: validated.value.y,
          width: validated.value.width,
          height: validated.value.height,
          color: validated.value.color as NoteColor,
          text: validated.value.text,
        }),
      );
    },
    [clientToBoard, dispatch, selectedColor],
  );

  const cancelCreate = useCallback(() => {
    setPreview(null);
  }, []);

  const onBoardPointerDown = useDrag<CreateCache>({
    onStart: startCreate,
    onDrag: duringCreate,
    onEnd: finishCreate,
    onCancel: cancelCreate,
  });

  const guardedPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return; // ignore clicks on children
      onBoardPointerDown(event);
    },
    [onBoardPointerDown],
  );

  const previewValid = preview
    ? sizeSatisfies(preview, MIN_NOTE_SIZE)
    : false;

  return (
    <div
      ref={boardRef}
      className={styles.board}
      onPointerDown={guardedPointerDown}
      role="application"
      aria-label="Sticky notes board — click and drag on empty space to create a note"
    >
      {notes.map((note) => (
        <Note key={note.id} note={note} />
      ))}
      <CreationPreview rect={preview} valid={previewValid} />
      <TrashZone />
    </div>
  );
}
